import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Modal, SegmentedControl, Textarea } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_CARRIER_CURATION_STATUS } from 'src/graphql/carriers.graphql'
import {
  CARRIER_CURATION_STATUS_DESCRIPTION,
  CARRIER_CURATION_STATUS_LABEL,
  CarrierCurationStatus,
} from 'src/graphql/types/carriers'

import type { CarrierDetailRecord } from '../carrier.types'
import { carrierName } from '../carrier.utils'

export interface CarrierCurationControlProps {
  carrier: CarrierDetailRecord
  /** Re-reads the carrier after a change; the Apollo cache does not merge Carrier. */
  onSaved: () => void
}

const RUNGS = [
  CarrierCurationStatus.UNREVIEWED,
  CarrierCurationStatus.REVIEWED,
  CarrierCurationStatus.LOCKED,
]

/**
 * Moves a carrier along the curation ladder from the page header.
 *
 * Picking a rung does not save it. The status decides what every driver
 * application, VoE form and voice-agent call may do to this record from now
 * on, so the change is confirmed in a dialog that spells that out and takes a
 * note for the colleague who finds the badge later.
 */
const CarrierCurationControl = ({
  carrier,
  onSaved,
}: CarrierCurationControlProps) => {
  const [pending, setPending] = useState<CarrierCurationStatus | null>(null)
  const [note, setNote] = useState('')

  const [setStatus, { loading }] = useMutation(UPDATE_CARRIER_CURATION_STATUS, {
    onCompleted: () => {
      toast('Curation status updated', 'success')
      setPending(null)
      onSaved()
    },
    onError: (error) => toast(error.message, 'error'),
  })

  const choose = (value: string) => {
    if (value === carrier.curationStatus) return
    setNote(carrier.curationNote ?? '')
    setPending(value as CarrierCurationStatus)
  }

  const confirm = () =>
    setStatus({
      variables: {
        carrierId: carrier.carrierId,
        status: pending,
        note: note.trim() || null,
      },
    })

  return (
    <div className="flex flex-col items-end gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-doubleNickel-gray-500">
        Directory writes
      </span>
      <SegmentedControl
        size="xs"
        value={carrier.curationStatus}
        onChange={choose}
        aria-label="Curation status"
        data={RUNGS.map((status) => ({
          value: status,
          label: CARRIER_CURATION_STATUS_LABEL[status],
        }))}
      />

      <Modal
        opened={pending !== null}
        onClose={() => setPending(null)}
        title={
          pending
            ? `Mark ${carrierName(carrier)} as ${
                CARRIER_CURATION_STATUS_LABEL[pending]
              }?`
            : ''
        }
        centered
      >
        {pending && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="text-doubleNickel-gray-600">
              {CARRIER_CURATION_STATUS_DESCRIPTION[pending]}
            </div>
            <Textarea
              id="carrier-curation-note"
              label="Note"
              description="Why this carrier is at this rung. Shown to anyone who opens the record."
              placeholder="e.g. Carrier asked us to stop emailing; verifications go through Tenstreet"
              autosize
              minRows={2}
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
            />
            <div className="flex justify-end gap-2">
              <DNButton
                variant="transparent"
                type="button"
                onClick={() => setPending(null)}
              >
                Cancel
              </DNButton>
              <DNButton
                variant="primary"
                type="button"
                loading={loading}
                onClick={confirm}
              >
                Mark as {CARRIER_CURATION_STATUS_LABEL[pending]}
              </DNButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default CarrierCurationControl
